import { type ReactNode, useEffect, useState } from 'react';
import Loading from '@/components/shared/Loading';

export interface EnvConfig {
  environment: string;
  appInsightsConnectionString: string;
  dbTenantId?: string;
  collaborationServerUrl?: string;
}

let cachedEnvConfig: EnvConfig | null = null;
let inflight: Promise<EnvConfig> | null = null;

export async function loadEnvConfig(): Promise<EnvConfig> {
  if (cachedEnvConfig) return cachedEnvConfig;
  if (inflight) return inflight;

  inflight = (async () => {
    const res = await fetch('/api/env-config');
    if (!res.ok) {
      throw new Error(`Failed to load runtime env config: ${res.status} ${res.statusText}`);
    }
    const raw = await res.json();
    cachedEnvConfig = {
      environment: raw.environment,
      appInsightsConnectionString: raw.app_insights_connection_string,
      dbTenantId: raw.db_tenant_id,
      collaborationServerUrl: raw.collaboration_server_url || import.meta.env.VITE_CRIMSON_COLLABORATION_WS_URL,
    };
    return cachedEnvConfig;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Synchronous accessor for the loaded env config. Use everywhere — components,
 * hooks, plain modules. Throws if called before <EnvConfigBootstrap /> has
 * resolved, which (since the bootstrap gates rendering) means it only throws
 * if called from a context that mounts outside the bootstrap.
 */
export function getEnvConfig(): EnvConfig {
  if (!cachedEnvConfig) {
    throw new Error('Runtime env config not loaded yet. Ensure <EnvConfigBootstrap /> has rendered before this call.');
  }
  return cachedEnvConfig;
}

interface EnvConfigBootstrapProps {
  children: ReactNode;
  onLoaded?: (envConfig: EnvConfig) => void;
}

/**
 * Blocks rendering of children until /api/env-config has resolved into
 * the module-level cache. Children render afterward, at which point any call
 * to getEnvConfig() (in a component or a plain module) returns synchronously.
 */
export function EnvConfigBootstrap({ children, onLoaded }: EnvConfigBootstrapProps) {
  const [loaded, setLoaded] = useState<boolean>(cachedEnvConfig !== null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (cachedEnvConfig) return;
    let cancelled = false;
    loadEnvConfig()
      .then((c) => {
        if (cancelled) return;
        setLoaded(true);
        onLoaded?.(c);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [onLoaded]);

  if (error) return <EnvConfigErrorScreen error={error} />;
  if (!loaded) return <div className="flex items-center justify-center h-screen"><Loading /></div>;
  return <>{children}</>;
}

const EnvConfigErrorScreen = ({ error }: { error: Error }) => {
  return (
    <div className="flex flex-col items-center justify-center h-screen p-6 text-center">
      <h1 className="text-xl font-semibold mb-2">Service unavailable</h1>
      <p className="text-gray-600 mb-4">Failed to load application configuration. Please try again in a moment.</p>
      <p className="text-xs text-gray-400">{error.message}</p>
    </div>
  );
}
